import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { FinallFirmRegisterModel } from '../models/firm-register/firm-register-final.model';
import { User } from 'src/app/views/register/register.component';
import { BodyNullResponse } from '../models/responses/body-null-response.model';
import { BodyUserDataResponse } from '../models/responses/body-user-response.model';
import { GoogleRegister, UserFromBE, UserPreferences } from '@sameta/shared';
import { NotificationService } from './notification.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { TranslateLanguageService } from './tanslate-language.service';

export interface UserType {
  createdAt: string;
  deletedAt: string;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  password: string;
  session: string;
  tenant: string;
  updatedAt: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  currentUser$: Observable<UserFromBE>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserFromBE>;
  isLoadingSubject: BehaviorSubject<boolean>;

  loginURL = 'api/login';
  logoutURL = 'api/logout';
  verifyURL = 'verify';
  verifyEmailURL = 'email-verify';
  registerURL = 'api/auth/registration/user';
  googleRegisterURL = 'api/auth/google-registration/user';
  registerCompanyURL = 'register-company';
  requestPasswordResetURL = 'api/request-password-reset';
  resetPasswordURL = 'api/reset-password';
  validateResetTokenURL = 'api/reset-password-token-validator';
  registerInvitedUserURL = 'api/auth/registration/invite-user';
  updatePreferencesURL = 'update-preferences';
  updateSolePreferenceURL = 'update-sole-preference';
  updateAuthTokenURL = 'api/auth/update-auth-token';

  public authSource = new Subject<boolean>();
  authObservable = this.authSource.asObservable();
  private authenticated = false;
  user: UserType;
  userFromBE: UserFromBE;
  afterPasswordReset = false;

  public get currentUserValue(): UserFromBE {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserFromBE) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly fireAuth: AngularFireAuth,
    private readonly translateLang: TranslateLanguageService,
    private readonly ngZone: NgZone,
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<any>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();

    // Initialize auth state on app startup/refresh
    this.initAuthState();

    // SAM-027: cross-tab logout sync. When another tab calls fireAuth.signOut(),
    // Firebase's IndexedDB-based auth state replicates here and emits null.
    // We then clean up local app state so this tab redirects to login instead
    // of continuing to render a dashboard with a dead session.
    this.fireAuth.authState.subscribe((user) => {
      if (!user && this.currentUserValue) {
        // Firebase reports no user but we still have local state → another tab logged out.
        this.ngZone.run(() => this.handleCrossTabLogout());
      }
    });
  }

  /**
   * Cleans up local app state when Firebase reports the user is signed out
   * (typically because another tab called signOut). Does NOT call Firebase
   * signOut itself — that already happened in the originating tab.
   */
  private handleCrossTabLogout(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.clear();
    }
    this.currentUserSubject.next(null);
    this.userFromBE = null;
    this.router.navigate(['/']);
  }

  private getHTTPHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json');
  }

  register(user: User): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.http.post(environment.openApiUrl + this.registerURL, user);
  }

  googleRegister(user: GoogleRegister) {
    this.isLoadingSubject.next(true);
    return this.http.post(environment.openApiUrl + this.googleRegisterURL, user);
  }

  registerInvitedUser(user: User, hash: string): Observable<any> {
    let queryParams = new HttpParams();
    this.isLoadingSubject.next(true);
    queryParams = queryParams.append('inviteHash', hash ? hash.toString() : '');
    return this.http.post(environment.openApiUrl + this.registerInvitedUserURL, user, { params: queryParams });
  }

  resetPassword(password: string, token: string) {
    const data = {
      password: password,
      token: token,
    };

    let queryParams = new HttpParams();

    queryParams = queryParams.append('password', password);
    queryParams = queryParams.append('token', token);
    return this.http.post<any>(environment.openApiUrl + this.resetPasswordURL, data, { params: queryParams });
  }

  isLoggedIn(): boolean {
    const user = localStorage.getItem('user');
    return user !== null;
  }

  login(idToken: string, reaction?: boolean): Observable<UserFromBE> {
    this.isLoadingSubject.next(true);

    return this.loginUser(idToken, reaction).pipe(
      map((res: UserFromBE) => {
        this.authenticated = true;
        this.userFromBE = res;
        this.currentUserValue = res;

        const userPreferences = res.userPreferences as UserPreferences;
        const userLanguage = this.userFromBE.language || 'en';

        // Update language in localStorage and trigger language change
        this.translateLang.language = userLanguage;

        localStorage.setItem('user', JSON.stringify(this.userFromBE));
        localStorage.setItem('preferences', userPreferences ? JSON.stringify(userPreferences) : '{}');
        localStorage.setItem('session', idToken);

        return res;
      }),
      catchError((err): Observable<UserFromBE> => {
        return throwError(() => err);
      })
    );
  }

  loginUser(idToken: string, reaction?: boolean): Observable<UserFromBE> {
    const httpHeaders = { authorization: idToken };
    let queryParams = new HttpParams();
    queryParams = queryParams.append('type', 'HR');

    return this.http.post<UserFromBE>(environment.openApiUrl + this.loginURL, undefined, { headers: httpHeaders, params: queryParams });
  }

  verifyUser(): Observable<any> {
    return this.http.get(environment.apiUrl + this.verifyURL, null);
  }

  verifyEmail(token: string): Observable<BodyUserDataResponse> {
    let queryParams = new HttpParams();

    queryParams = queryParams.append('token', token);
    return this.http.put<BodyUserDataResponse>(environment.apiUrl + this.verifyEmailURL, null, { params: queryParams });
  }

  signOut(): Observable<BodyUserDataResponse> {
    const httpHeaders = this.getHTTPHeaders();
    return this.http.put<BodyUserDataResponse>(environment.openApiUrl + this.logoutURL, {
      headers: httpHeaders,
    });
  }

  createNewCompany(data: FinallFirmRegisterModel): Observable<any> {
    const httpHeaders = this.getHTTPHeaders();
    return this.http.post<any>(environment.apiUrl + this.registerCompanyURL, JSON.stringify(data), { headers: httpHeaders });
  }

  requestPasswordReset(email: string, language: string): Observable<any> {
    let queryParams = new HttpParams();

    queryParams = queryParams.append('email', email);
    queryParams = queryParams.append('language', language);
    const data = email;
    return this.http.post<any>(environment.openApiUrl + this.requestPasswordResetURL, data, { params: queryParams });
  }

  toLoginAfterPasswordReset(): void {
    this.afterPasswordReset = true;
    this.router.navigate(['']);
  }

  validateResetToken(token: string): Observable<BodyUserDataResponse> {
    let queryParams = new HttpParams();

    queryParams = queryParams.append('token', token);
    const data = {
      token: token,
    };
    return this.http.post<any>(environment.openApiUrl + this.validateResetTokenURL, data, { params: queryParams });
  }

  updatePreferences(userPreferences: UserPreferences): Observable<BodyNullResponse> {
    return this.http.post<any>(environment.apiUrl + this.updatePreferencesURL, userPreferences);
  }

  updateSolePreference(preference: string, preferenceValue: string): Observable<BodyUserDataResponse> {
    let queryParams = new HttpParams();

    queryParams = queryParams.append('preference', preference ? preference : '');
    queryParams = queryParams.append('preferenceValue', preferenceValue ? preferenceValue : '');

    return this.http.post<any>(environment.apiUrl + this.updateSolePreferenceURL, '', { params: queryParams });
  }

  /**
   * Initialize authentication state on application startup or page refresh
   * Token refresh is now handled by the error interceptor when AUTHORIZATION_EXPIRED is received
   */
  private initAuthState(): void {
    // Monitor Firebase auth state changes
    this.fireAuth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        // Firebase user is available - check if we have corresponding app user data
        if (this.isLoggedIn()) {
          const userData = localStorage.getItem('user');
          if (userData) {
            try {
              const user = JSON.parse(userData);
              this.userFromBE = user;
              this.currentUserSubject.next(user);
              this.authenticated = true;
            } catch (e) {
              console.error('Error parsing user data from localStorage', e);
            }
          }
        }
      } else {
        // If we have session data but no Firebase user, this might indicate an issue
        if (this.isLoggedIn()) {
          // Warning: Local session exists but no Firebase user - this may cause token refresh issues
        }
      }
    });

    // Initial check for existing session
    if (this.isLoggedIn()) {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          this.userFromBE = user;
          this.currentUserSubject.next(user);
          this.authenticated = true;
        } catch (e) {
          console.error('Error parsing user data from localStorage', e);
        }
      }
    }
  }

  updateAuthToken(token: string): Observable<BodyUserDataResponse> {
    let queryParams = new HttpParams();

    queryParams = queryParams.append('token', token);
    const data = {
      token: token,
    };
    return this.http.put<any>(environment.openApiUrl + this.updateAuthTokenURL, data, { params: queryParams });
  }
}
